import { useEffect, useState } from 'react';
import { formatCents, type SalesReport, type ShiftReportRow } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { reportsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { ImpersonationTarget } from '../../state/impersonationStore.js';
import { Banner } from '../../components/Banner.js';
import { StatTile } from '../../components/StatTile.js';
import { formatDateTime, daysAgoISO, todayISO } from '../../lib/format.js';

export interface ImpersonationViewProps {
  target: ImpersonationTarget;
}

/**
 * A read-only reflection of what this staff member would see: their own
 * sales for the day and their own shift history, with opening/closing
 * balances but no expected cash and no variance — because the staff-facing
 * app never shows those either (spec section 7). This view exists inside
 * the admin SPA rather than embedding the POS app itself; see
 * docs/decisions/2026-07-31-impersonation-scope.md.
 */
export function ImpersonationView({ target }: ImpersonationViewProps) {
  const { t } = useI18n();
  const [today, setToday] = useState<SalesReport | null>(null);
  const [shifts, setShifts] = useState<ShiftReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      reportsApi.sales({ from: todayISO(), to: todayISO(), staffId: target.id }),
      reportsApi.shifts(daysAgoISO(14), todayISO()),
    ])
      .then(([report, shiftRows]) => {
        if (cancelled) return;
        setToday(report);
        setShifts(shiftRows.filter((s) => s.staffId === target.id));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target.id]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{target.displayName}</h1>
          <p>{t('staff.code')}: {target.code}</p>
        </div>
      </div>

      <Banner kind="warn">{t('impersonation.confirmBody')}</Banner>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <>
          {today && (
            <div className="stat-grid">
              <StatTile label={t('reports.netSales')} value={formatCents(today.netSalesCents)} />
              <StatTile label={t('reports.transactions')} value={String(today.transactionCount)} />
              <StatTile label={t('reports.itemsSold')} value={String(today.itemsSold)} />
            </div>
          )}

          <div className="card">
            <h2>{t('shifts.title')}</h2>
            {shifts.length === 0 ? (
              <p>{t('shifts.noData')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('shifts.opened')}</th>
                      <th>{t('shifts.closed')}</th>
                      <th className="numeric">{t('shifts.openingBalance')}</th>
                      <th className="numeric">{t('shifts.closingBalance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((s) => (
                      <tr key={s.shiftId}>
                        <td className="numeric">{formatDateTime(s.openedAt)}</td>
                        <td className="numeric">{s.closedAt ? formatDateTime(s.closedAt) : t('shifts.statusOpen')}</td>
                        <td className="numeric">{formatCents(s.openingBalanceCents)}</td>
                        <td className="numeric">
                          {s.closingBalanceCents != null ? formatCents(s.closingBalanceCents) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
